import React, { useState } from "react";
import { FaCirclePlus, FaTrash } from "react-icons/fa6";
import { Button, Input } from "reactstrap";
import styles from "./UploadMedia.module.scss";

const UploadMedia = ({
  pictures,
  setPictures,
  allowImages,
  allowVideos,
  showUrlInput,
  maxMedia,
  fileInputRef,
  hideUploadLabel,
}) => {
  const [mediaFiles, setMediaFiles] = useState([]); // Store file info with type
  const [errorMessage, setErrorMessage] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (mediaFiles.length + selectedFiles.length > maxMedia) {
      setErrorMessage(
        `You can only upload a maximum of ${maxMedia} media files.`
      );
      return; // Exit if the limit is reached
    }

    if (selectedFiles.length > 0) {
      setErrorMessage("");
      const newPictures = [...pictures, ...selectedFiles];
      setPictures(newPictures);

      // Create an array of objects to store both URL and type
      const newMediaFiles = selectedFiles.map((file) => ({
        url: URL.createObjectURL(file),
        type: file.type, // Store the file type (image/jpeg, video/mp4, etc.)
      }));

      setMediaFiles((prevFiles) => [...prevFiles, ...newMediaFiles]);
    }

    e.target.value = ""; // Clear the file input
  };

  const handleRemoveFile = (indexToRemove) => {
    const updatedPictures = pictures.filter(
      (_, index) => index !== indexToRemove
    );
    setPictures(updatedPictures);

    const updatedMediaFiles = mediaFiles.filter(
      (_, index) => index !== indexToRemove
    );
    setMediaFiles(updatedMediaFiles);
  };

  const handleUrlAdd = () => {
    if (videoUrl && mediaFiles.length < maxMedia) {
      let embedUrl = videoUrl;

      // Check if the URL is a YouTube link
      const youtubeMatch = videoUrl.match(
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
      );
      if (youtubeMatch) {
        embedUrl = `https://www.youtube.com/embed/${youtubeMatch[1]}`;
      }

      // Check if the URL is a TikTok link
      const tiktokMatch = videoUrl.match(
        /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@([a-zA-Z0-9._-]+)\/video\/([0-9]+)/
      );
      if (tiktokMatch) {
        embedUrl = `https://www.tiktok.com/embed/v2/${tiktokMatch[2]}`;
      }

      // Check if the URL is a Facebook link
      const facebookMatch = videoUrl.match(
        /(?:https?:\/\/)?(?:www\.)?facebook\.com\/(?:[^\/]+\/videos\/|watch\/\?v=)([0-9]+)/
      );
      if (facebookMatch) {
        embedUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(videoUrl)}`;
      }

      // Check if the URL is an Instagram link
      const instagramMatch = videoUrl.match(
        /(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|tv|reel)\/([a-zA-Z0-9_-]+)/
      );
      if (instagramMatch) {
        embedUrl = `https://www.instagram.com/p/${instagramMatch[1]}/embed/`;
      }

      const updatedMediaFiles = [
        ...mediaFiles,
        { type: "url", url: embedUrl }, // Add URL as type 'url'
      ];

      setMediaFiles(updatedMediaFiles);
      setVideoUrl("");
    }
  };

  return (
    <div className={styles.fileUploaderContainer}>
      <div className={styles.imageFileUploader}>
        {mediaFiles.length < maxMedia && (
          <label
            className={styles.uploadLabel}
            hidden={hideUploadLabel}
          >
            <FaCirclePlus size={21} />
            <Input
              type="file"
              id="uploadFile"
              onChange={handleImageChange}
              innerRef={fileInputRef}
              className={styles.inputfile}
              accept={
                allowVideos && allowImages
                  ? "image/*,video/*"
                  : allowVideos
                    ? "video/*"
                    : "image/*"
              }
              multiple
            />
          </label>
        )}
        <div className={styles.uploadedMedia}>
          {mediaFiles.map((file, index) => (
            <div key={index} className={styles.mediaContainer}>
              {file.type.startsWith("video") ? (
                <video
                  src={file.url}
                  className={styles.videoPreview}
                  autoPlay
                  muted
                  playsInline
                  onLoadStart={(e) =>
                    e.target.setAttribute("preload", "metadata")
                  }
                />
              ) : file.type === "url" ? ( // Check if the file is a URL
                <iframe
                  src={file.url}
                  className={styles.videoPreview}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Video"
                ></iframe>
              ) : (
                <img
                  src={file.url}
                  alt="media"
                  className={styles.imagePreview}
                />
              )}
              <Button
                color="danger"
                outline
                size="sm"
                className={styles.removeButton}
                onClick={() => handleRemoveFile(index)}
              >
                <FaTrash size={14} />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {showUrlInput && (
        <div className={styles.urlInputSection}>
          <p>Add Videos through product URL</p>
          <div md="12" className={styles.urlInputContainer}>
            <Input
              type="text"
              placeholder="Add Product Video URL"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className={styles.urlInput}
            />
            <Button onClick={handleUrlAdd} className={styles.urlAddButton}>
              Add
            </Button>
          </div>
        </div>
      )}

      {/* Display error message below the uploader */}
      {errorMessage && <p className={styles.errorText}>{errorMessage}</p>}
    </div>
  );
};

export default UploadMedia;